import { Attachment, DeleteAttachmentPayload } from '@api/models'
import {
  AttachmentApi,
  useDeleteAttachmentMutation,
  useGetInsuranceAttachmentsQuery,
} from '@api'
import {
  ReactSelectOption,
  ReactSelectOptions,
} from '@components/common/Select/Select.types'
import { useEffect, useState } from 'react'
import { toast } from '@components/common/Toasts'

interface UseInsuranceAttachmentHookProps {
  insuranceID: string
}

const useInsuranceAttachmentHook = ({ insuranceID }: UseInsuranceAttachmentHookProps) => {
  const [options, setOptions] = useState<Attachment[]>([])
  const [selectedOption, setSelectedOption] = useState<ReactSelectOption>(null)
  const [attachment, setAttachment] = useState<{
    isLoading: boolean
    url: string
    details: Attachment
  }>(null)
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<string | null>(null)

  const [deleteAttachmentMutation] = useDeleteAttachmentMutation()

  const {
    isLoading: isInsuranceAttachmentsLoading,
    data: insuranceAttachments,
  } = useGetInsuranceAttachmentsQuery(
    {
      insuranceID,
    },
    {
      skip: !insuranceID, // Skip if no insurance ID provided
    }
  )

  let selectOptions: ReactSelectOptions = options.map(a => ({
    label: a.Comment,
    value: a.FileName,
  }))

  // initialize options when insurance attachments are loaded
  useEffect(() => {
    if (isInsuranceAttachmentsLoading || !insuranceAttachments) return

    setOptions(insuranceAttachments)
    if (insuranceAttachments.length > 0) {
      setSelectedOption({
        label: insuranceAttachments[0].Comment,
        value: insuranceAttachments[0].FileName,
      })
    } else {
      setSelectedOption(null)
    }
  }, [insuranceAttachments, isInsuranceAttachmentsLoading])

  // download attachment and parse into blob
  useEffect(() => {
    if (selectedOption === null) {
      setAttachment(null)
    } else {
      setAttachment({ url: '', isLoading: true, details: null })
      AttachmentApi.getInstance()
        .download(selectedOption.value)
        .then((res: any) => {
          setAttachment({
            url: URL.createObjectURL(new Blob([res.data])),
            isLoading: false,
            details: insuranceAttachments.find(a => a.FileName === selectedOption.value),
          })
        })
        .catch(() => {
          setAttachment(null)
        })
    }
  }, [selectedOption, insuranceAttachments])

  function handleAttachmentSelection(option: ReactSelectOption) {
    setSelectedOption(option)
  }

  const deleteAttachment = async (params: DeleteAttachmentPayload) => {
    setDeletingAttachmentId(params.id)
    try {
      await deleteAttachmentMutation(params).unwrap()
      toast.success('Attachment deleted successfully')
    } catch (error) {
      toast.error('Failed to delete attachment')
      throw error
    } finally {
      setDeletingAttachmentId(null)
    }
  }

  function isAttachmentDeleting(attachmentId: string): boolean {
    return deletingAttachmentId === attachmentId
  }

  return {
    selectOptions,
    options, // Return the raw attachment options array
    isLoading: isInsuranceAttachmentsLoading,
    selectedOption,
    handleAttachmentSelection,
    attachment,
    insuranceID,
    deleteAttachment,
    isAttachmentDeleting,
  }
}

export default useInsuranceAttachmentHook
